import { useState } from "react";
import { MapPin, Phone, Mail, Globe, User, Calendar, ArrowUpRight, ChevronRight, Star } from "lucide-react";

const d = {
  masthead_left: "Volume III · Issue 07",
  masthead_right: "Higher Education Directory",
  category: "Business Schools · India",
  full_name: "Indian Institute of Management",
  city: "Ahmedabad",
  strapline: "Six decades on, the case-method campus that reshaped Indian management.",
  founded: 1961,
  location: "Vastrapur · Ahmedabad · Gujarat",
  hero: "https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=2200&auto=format&fit=crop",
  hero_caption: "Louis Kahn's brick vaults on a monsoon evening — the architectural signature of an institute that treats design as pedagogy.",
  intro_lead: "Founded in 1961 with support from the Ford Foundation and the Harvard Business School, IIMA established the case-method rigour that defined Indian management education.",
  intro_body: `Today, its 106-acre campus of red-brick colonnades — designed by Louis I. Kahn and B. V. Doshi — is a national landmark and a global architectural pilgrimage. Alumni run Fortune 500 companies, unicorn startups, ministries and multilateral banks. Applicants across South Asia sit for the CAT with a single question in mind: does the letter arrive?

Beneath the mystique, the pedagogy is deliberately unsentimental. Every case is dissected in a 70-student pit, every judgement stress-tested by peers before dawn. The result is a graduate who thinks in decisions, not decks — and a network that answers the phone at midnight, thirty years on.`,
  pull_quote: "IIMA didn't teach me answers. It taught me how to ask better questions. Twenty years in, that's still the thing I use every day.",
  pull_author: "Anjali Menon · PGP '04 · Partner, McKinsey",
  vitals: [
    { k: "Est.", v: "1961" },
    { k: "Rank", v: "#1", suffix: "NIRF · Management" },
    { k: "Faculty", v: "108" },
    { k: "Median CTC", v: "₹34L" },
    { k: "Placement", v: "100%" },
    { k: "Selectivity", v: "0.4%" },
    { k: "Alumni", v: "45k+" },
    { k: "Accreditation", v: "Triple", suffix: "AACSB · EQUIS · AMBA" },
  ],
  programmes: [
    { name: "Post-Graduate Programme (PGP)", meta: "2 yr · full-time · residential", desc: "The flagship. 395 seats. Median CTC ₹34L. Case-method core, global exchange in Y2." },
    { name: "PGP for Executives (PGPX)", meta: "1 yr · residential", desc: "Accelerated MBA for 5-15 year professionals. Cohorts drawn from 25+ nationalities." },
    { name: "Food & Agri-Business (FABM)", meta: "2 yr · specialist track", desc: "India's oldest dedicated agri-management degree. Deep supply-chain immersion." },
    { name: "Fellow Programme (FPM)", meta: "4–6 yr · doctoral · funded", desc: "PhD in management with 12 specialisations. Two publishable papers required to graduate." },
    { name: "Advanced Management Programme", meta: "5 wk residential", desc: "For C-suite. Case-method, personal coaching, board simulations." },
    { name: "Chief Digital Officer", meta: "12 mo · blended", desc: "Post-graduate certificate for senior technology leaders." },
  ],
  gallery: [
    { src: "https://images.unsplash.com/photo-1562774053-701939374585?w=1200&auto=format&fit=crop", caption: "The Louis Kahn Plaza — where every case discussion still spills out at dusk." },
    { src: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?w=900&auto=format&fit=crop", caption: "The Vikram Sarabhai Library, 200,000 volumes and counting." },
    { src: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?w=900&auto=format&fit=crop", caption: "Chaos, the annual cultural festival, draws 25,000 visitors across three days." },
    { src: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200&auto=format&fit=crop", caption: "Study groups in the dorm blocks — the case prep never stops after 10 pm." },
    { src: "https://images.unsplash.com/photo-1543269865-cbf427effbad?w=900&auto=format&fit=crop", caption: "The Olympic-size pool: as much a rite of passage as final examinations." },
    { src: "https://images.unsplash.com/photo-1562075929-97ded9c1731a?w=900&auto=format&fit=crop", caption: "Convocation on the plaza — 40 faculty, 550 graduates, a single ceremony." },
  ],
  recruiters: ["McKinsey", "BCG", "Bain", "Goldman Sachs", "Amazon", "Google", "Microsoft", "ITC", "HUL", "Reliance", "Tata Sons", "Meta"],
  admissions: [
    { m: "Jul", label: "CAT registrations open" },
    { m: "Nov", label: "CAT examination" },
    { m: "Jan", label: "Shortlists announced" },
    { m: "Mar", label: "Personal Interview + WAT" },
    { m: "May", label: "Final offers extended" },
    { m: "Jun", label: "Programme begins" },
  ],
  contact: {
    address: "Vastrapur, Ahmedabad — 380 015, Gujarat, India",
    phone: "+91 79 6632 4001",
    email: "admissions@iima.ac.in",
    website: "www.iima.ac.in",
    director: "Prof. Bharat Bhasker",
  },
};

// Design tokens
const CREAM = "#f6ecd7";
const CREAM_DEEP = "#eddfbf";
const INK = "#2a1e10";
const INK_SOFT = "#4a3a24";
const GOLD = "#a97b31";
const GOLD_SOFT = "#c9a05b";
const WINE = "#752132";

export default function CollegeProfileSamplePage() {
  const [tabIdx, setTabIdx] = useState(0);
  const tabbedProgs = [d.programmes.slice(0, 3), d.programmes.slice(3, 5), d.programmes.slice(5)];
  const tabLabels = ["Post-Graduate", "Doctoral & FPM", "Executive"];

  return (
    <div className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "Georgia, 'Times New Roman', serif" }} data-testid="college-profile-sample">
      {/* ================= MASTHEAD ================= */}
      <div className="border-b" style={{ borderColor: `${INK}22` }}>
        <div className="max-w-6xl mx-auto px-8 py-4 flex justify-between items-center text-[11px] tracking-[0.3em] uppercase" style={{ color: INK_SOFT }}>
          <span>{d.masthead_left}</span>
          <div className="flex items-center gap-3">
            <span className="w-8 h-px" style={{ background: GOLD_SOFT }}/>
            <span style={{ color: GOLD }} className="font-bold">The Directory</span>
            <span className="w-8 h-px" style={{ background: GOLD_SOFT }}/>
          </div>
          <span>{d.masthead_right}</span>
        </div>
      </div>

      {/* ================= TITLE BLOCK ================= */}
      <header className="max-w-6xl mx-auto px-8 pt-16 pb-8 text-center relative">
        <div className="text-[11px] tracking-[0.35em] uppercase mb-6" style={{ color: GOLD }}>{d.category}</div>
        <div className="flex justify-center items-center gap-4 mb-6" style={{ color: GOLD_SOFT }}>
          <span className="w-16 h-px" style={{ background: GOLD_SOFT }}/>
          <Ornament/>
          <span className="w-16 h-px" style={{ background: GOLD_SOFT }}/>
        </div>
        <h1 className="font-editorial font-black leading-[0.9] tracking-tight" style={{ color: INK, fontSize: "clamp(3rem, 7vw, 6rem)" }}>
          {d.full_name}
          <br/>
          <span style={{ color: WINE, fontStyle: "italic" }}>{d.city}</span>
        </h1>
        <div className="mt-6 max-w-3xl mx-auto text-xl italic leading-relaxed" style={{ color: INK_SOFT }}>
          "{d.strapline}"
        </div>
        <div className="mt-8 flex flex-wrap justify-center gap-x-8 gap-y-2 text-xs tracking-[0.15em] uppercase" style={{ color: INK_SOFT }}>
          <span className="flex items-center gap-1"><MapPin className="w-3 h-3"/>{d.location}</span>
          <span className="flex items-center gap-1"><Calendar className="w-3 h-3"/>Established {d.founded}</span>
          <span className="flex items-center gap-1"><Star className="w-3 h-3" style={{ color: GOLD }}/>Verified profile</span>
        </div>
      </header>

      {/* ================= HERO IMAGE ================= */}
      <section className="max-w-6xl mx-auto px-8 pb-14">
        <div className="relative overflow-hidden shadow-2xl" style={{ border: `1px solid ${INK}25` }}>
          <img src={d.hero} alt="" className="w-full h-[540px] object-cover"/>
          <div className="absolute inset-x-0 bottom-0 p-6 md:p-8 text-white" style={{ background: "linear-gradient(to top, rgba(20,10,0,0.85), transparent)" }}>
            <div className="text-[10px] tracking-[0.3em] uppercase mb-1" style={{ color: GOLD_SOFT }}>Cover · Campus</div>
            <p className="italic text-base md:text-lg max-w-3xl leading-snug">{d.hero_caption}</p>
          </div>
        </div>
      </section>

      {/* ================= LEAD SPREAD ================= */}
      <section className="max-w-6xl mx-auto px-8 pb-20 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-8">
          <div className="text-[11px] tracking-[0.3em] uppercase mb-3" style={{ color: GOLD }}>The Story</div>
          <h2 className="font-editorial text-3xl md:text-4xl font-bold leading-tight mb-6" style={{ color: INK }}>An institute built on rigour, restraint, and red brick.</h2>
          <p className="text-lg leading-[1.75] first-letter:font-editorial first-letter:text-6xl first-letter:font-black first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:mt-1" style={{ color: INK }}>
            {d.intro_lead}
          </p>
          <p className="text-lg leading-[1.75] mt-4 whitespace-pre-line" style={{ color: INK }}>{d.intro_body}</p>

          <figure className="my-10 py-6 border-y-2" style={{ borderColor: `${GOLD_SOFT}` }}>
            <blockquote className="font-editorial text-2xl md:text-3xl italic leading-snug text-center px-4" style={{ color: WINE }}>
              &ldquo;{d.pull_quote}&rdquo;
            </blockquote>
            <figcaption className="mt-4 text-center text-[11px] tracking-[0.25em] uppercase" style={{ color: INK_SOFT }}>— {d.pull_author}</figcaption>
          </figure>
        </div>

        {/* Sidebar: Vital Statistics */}
        <aside className="md:col-span-4">
          <div className="sticky top-8 p-8 border-2" style={{ borderColor: INK, background: CREAM_DEEP }}>
            <div className="text-center pb-4 mb-4 border-b" style={{ borderColor: `${INK}33` }}>
              <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: GOLD }}>Vital Statistics</div>
              <div className="mt-3 flex justify-center gap-3" style={{ color: GOLD_SOFT }}>
                <span className="w-6 h-px" style={{ background: GOLD_SOFT }}/>
                <Ornament small/>
                <span className="w-6 h-px" style={{ background: GOLD_SOFT }}/>
              </div>
            </div>
            <dl className="space-y-4">
              {d.vitals.map(v => (
                <div key={v.k} className="flex items-baseline justify-between gap-4 border-b border-dotted pb-3 last:border-0" style={{ borderColor: `${INK}33` }}>
                  <dt className="text-[10px] tracking-[0.25em] uppercase" style={{ color: INK_SOFT }}>{v.k}</dt>
                  <dd className="text-right">
                    <div className="font-editorial font-black text-2xl" style={{ color: WINE }}>{v.v}</div>
                    {v.suffix && <div className="text-[10px] italic mt-0.5" style={{ color: INK_SOFT }}>{v.suffix}</div>}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </aside>
      </section>

      {/* ================= PROGRAMMES ================= */}
      <section className="border-y-2" style={{ borderColor: INK, background: CREAM_DEEP }}>
        <div className="max-w-6xl mx-auto px-8 py-16">
          <div className="text-center mb-10">
            <div className="text-[11px] tracking-[0.35em] uppercase mb-3" style={{ color: GOLD }}>Programmes</div>
            <h2 className="font-editorial text-4xl md:text-5xl font-bold" style={{ color: INK }}>What one can study, here.</h2>
            <div className="mt-6 flex justify-center gap-3" style={{ color: GOLD_SOFT }}>
              <span className="w-16 h-px" style={{ background: GOLD_SOFT }}/>
              <Ornament/>
              <span className="w-16 h-px" style={{ background: GOLD_SOFT }}/>
            </div>
          </div>

          <div className="flex justify-center flex-wrap gap-2 mb-10">
            {tabLabels.map((l, i) => (
              <button
                key={l}
                onClick={() => setTabIdx(i)}
                className="px-5 py-2 text-xs tracking-[0.2em] uppercase font-bold border-2 transition-colors"
                style={{
                  borderColor: tabIdx === i ? INK : `${INK}40`,
                  background: tabIdx === i ? INK : "transparent",
                  color: tabIdx === i ? CREAM : INK,
                }}
              >
                {l}
              </button>
            ))}
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {tabbedProgs[tabIdx].map(p => (
              <article key={p.name} className="relative p-6" style={{ background: CREAM, border: `1px solid ${INK}30` }}>
                <CornerFlourish/>
                <div className="text-[10px] tracking-[0.25em] uppercase mb-3" style={{ color: GOLD }}>{p.meta}</div>
                <h3 className="font-editorial text-xl font-bold leading-snug mb-3" style={{ color: INK }}>{p.name}</h3>
                <p className="text-sm leading-relaxed" style={{ color: INK_SOFT }}>{p.desc}</p>
                <a href="#" className="inline-flex items-center gap-1 mt-4 text-xs tracking-[0.2em] uppercase font-bold" style={{ color: WINE }}>
                  Learn more <ArrowUpRight className="w-3 h-3"/>
                </a>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PHOTO ESSAY ================= */}
      <section className="max-w-6xl mx-auto px-8 py-20">
        <div className="text-center mb-14">
          <div className="text-[11px] tracking-[0.35em] uppercase mb-3" style={{ color: GOLD }}>Photo Essay</div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold" style={{ color: INK }}>A day on the plaza.</h2>
        </div>
        <div className="grid md:grid-cols-6 gap-4 md:gap-6">
          {d.gallery.map((g, i) => (
            <figure key={i} className={`${i === 0 ? "md:col-span-4 md:row-span-2" : "md:col-span-2"}`}>
              <div className="overflow-hidden shadow-md" style={{ border: `1px solid ${INK}20` }}>
                <img src={g.src} alt="" className={`w-full ${i === 0 ? "h-[540px]" : "h-64"} object-cover hover:scale-105 transition-transform duration-700`}/>
              </div>
              <figcaption className="mt-3 text-xs italic leading-snug" style={{ color: INK_SOFT }}>
                <span style={{ color: GOLD }} className="font-bold not-italic">Fig. {i + 1} — </span>
                {g.caption}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ================= PLACEMENTS + RECRUITERS ================= */}
      <section style={{ background: INK, color: CREAM }} className="py-20">
        <div className="max-w-6xl mx-auto px-8 grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5">
            <div className="text-[11px] tracking-[0.35em] uppercase mb-3" style={{ color: GOLD_SOFT }}>Placements · 2024</div>
            <h2 className="font-editorial text-4xl md:text-5xl font-bold leading-tight" style={{ color: CREAM }}>Where the letter lands.</h2>
            <div className="mt-10 space-y-5">
              <BigStat label="Median CTC" value="₹34.4 LPA" trend="+8.2 % YoY"/>
              <BigStat label="Highest domestic" value="₹90 LPA"/>
              <BigStat label="Highest international" value="US $ 185k"/>
              <BigStat label="Roles offered" value="612" trend="across 138 recruiters"/>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="text-[10px] tracking-[0.3em] uppercase mb-4" style={{ color: GOLD_SOFT }}>Recruiters of record</div>
            <div className="grid grid-cols-3 gap-3">
              {d.recruiters.map(r => (
                <div key={r} className="h-16 grid place-items-center font-editorial font-bold text-sm border" style={{ borderColor: `${CREAM}30`, color: CREAM }}>
                  {r}
                </div>
              ))}
            </div>
            <div className="mt-6 text-[11px] italic" style={{ color: `${CREAM}80` }}>Selection · not comprehensive · alphabetical.</div>
          </div>
        </div>
      </section>

      {/* ================= ADMISSIONS TIMELINE ================= */}
      <section className="max-w-6xl mx-auto px-8 py-20">
        <div className="text-center mb-14">
          <div className="text-[11px] tracking-[0.35em] uppercase mb-3" style={{ color: GOLD }}>The Cycle</div>
          <h2 className="font-editorial text-4xl md:text-5xl font-bold" style={{ color: INK }}>One year. Six milestones.</h2>
        </div>
        <div className="relative">
          <div className="absolute left-6 right-6 md:left-12 md:right-12 top-8 h-px" style={{ background: `${GOLD}66` }}/>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 relative">
            {d.admissions.map(t => (
              <div key={t.m} className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full border-2 grid place-items-center font-editorial font-black text-xl mb-3"
                     style={{ borderColor: GOLD, background: CREAM, color: WINE }}>
                  {t.m}
                </div>
                <div className="text-xs leading-snug" style={{ color: INK }}>{t.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= COLOPHON / CONTACT ================= */}
      <section style={{ background: CREAM_DEEP }} className="border-y-2 py-16" >
        <div className="max-w-6xl mx-auto px-8 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-4">
            <div className="text-[11px] tracking-[0.35em] uppercase mb-2" style={{ color: GOLD }}>Colophon</div>
            <h3 className="font-editorial text-3xl font-bold" style={{ color: INK }}>Address the Institute.</h3>
            <p className="mt-3 text-sm italic" style={{ color: INK_SOFT }}>All correspondence should be marked to the Admissions Office.</p>
          </div>
          <div className="md:col-span-5 space-y-3 text-sm" style={{ color: INK }}>
            <div className="flex gap-3"><MapPin className="w-4 h-4 mt-0.5" style={{ color: GOLD }}/><span>{d.contact.address}</span></div>
            <div className="flex gap-3"><Phone className="w-4 h-4 mt-0.5" style={{ color: GOLD }}/><a href={`tel:${d.contact.phone}`} className="hover:underline">{d.contact.phone}</a></div>
            <div className="flex gap-3"><Mail className="w-4 h-4 mt-0.5" style={{ color: GOLD }}/><a href={`mailto:${d.contact.email}`} className="hover:underline">{d.contact.email}</a></div>
            <div className="flex gap-3"><Globe className="w-4 h-4 mt-0.5" style={{ color: GOLD }}/><a href="#" className="hover:underline">{d.contact.website}</a></div>
            <div className="flex gap-3"><User className="w-4 h-4 mt-0.5" style={{ color: GOLD }}/><span>Director · {d.contact.director}</span></div>
          </div>
          <div className="md:col-span-3 flex md:justify-end">
            <a href="#" className="inline-flex flex-col items-center gap-1 px-8 py-6 border-2 hover:scale-105 transition-transform" style={{ borderColor: WINE, color: WINE, background: CREAM }}>
              <span className="text-[10px] tracking-[0.3em] uppercase">Request</span>
              <span className="font-editorial font-black text-2xl">Prospectus</span>
              <ChevronRight className="w-5 h-5 mt-1"/>
            </a>
          </div>
        </div>
      </section>

      {/* ================= FOOTER SIGNATURE ================= */}
      <footer className="max-w-6xl mx-auto px-8 py-10 text-center">
        <div className="flex justify-center gap-3 mb-4" style={{ color: GOLD_SOFT }}>
          <span className="w-16 h-px" style={{ background: GOLD_SOFT }}/>
          <Ornament/>
          <span className="w-16 h-px" style={{ background: GOLD_SOFT }}/>
        </div>
        <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: INK_SOFT }}>
          EduTime Today · The Higher Education Directory · MMXXVI
        </div>
      </footer>
    </div>
  );
}

function Ornament({ small = false }) {
  const s = small ? "w-3 h-3" : "w-4 h-4";
  return (
    <svg viewBox="0 0 24 24" className={s} fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z"/>
    </svg>
  );
}

function CornerFlourish() {
  return (
    <>
      <span className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2" style={{ borderColor: "#a97b31" }}/>
      <span className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2" style={{ borderColor: "#a97b31" }}/>
      <span className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2" style={{ borderColor: "#a97b31" }}/>
      <span className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2" style={{ borderColor: "#a97b31" }}/>
    </>
  );
}

function BigStat({ label, value, trend }) {
  return (
    <div className="flex items-baseline justify-between border-b pb-3" style={{ borderColor: "#f6ecd733" }}>
      <div className="text-xs tracking-[0.2em] uppercase" style={{ color: "#c9a05b" }}>{label}</div>
      <div className="text-right">
        <div className="font-editorial font-black text-3xl md:text-4xl" style={{ color: "#f6ecd7" }}>{value}</div>
        {trend && <div className="text-xs italic mt-0.5" style={{ color: "#c9a05b" }}>{trend}</div>}
      </div>
    </div>
  );
}
